import { useState, useEffect, useRef } from "react";
import { MapPin } from "lucide-react";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const GEOCODING_URL = "https://api.mapbox.com/geocoding/v5/mapbox.places";

/**
 * LocationAutocomplete — Mapbox-powered place/address search input.
 *
 * Props:
 *   value           (string)  — initial display value
 *   onSelect        (fn)      — called with the selected Mapbox GeoJSON feature
 *   onChange        (fn)      — called with the raw string on every keystroke
 *   placeholder     (string)
 *   types           (string)  — Mapbox types param (default: "poi,address")
 *   defaultProximity ({lat, lng}) — fallback bias when geolocation is unavailable
 */
export default function LocationAutocomplete({
  value = "",
  onSelect,
  onChange,
  placeholder = "Search for a location",
  types = "poi,address",
  defaultProximity = null,
}) {
  const [query, setQuery]     = useState(value);
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const [proximity, setProximity] = useState(defaultProximity);

  const containerRef    = useRef(null);
  const debounceRef     = useRef(null);
  const abortRef        = useRef(null);
  const cacheRef        = useRef(new Map());

  // Try to get the user's current location for proximity bias
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setProximity({ lng: pos.coords.longitude, lat: pos.coords.latitude }),
      () => {} // silently fall through to defaultProximity
    );
  }, []);

  // Sync initial value from parent (e.g. pre-filled edit form)
  useEffect(() => { setQuery(value); }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleOutsideClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  function handleInput(e) {
    const val = e.target.value;
    setQuery(val);
    onChange?.(val);

    clearTimeout(debounceRef.current);

    if (val.trim().length < 3 || !MAPBOX_TOKEN) {
      setResults([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => fetchSuggestions(val), 500);
  }

  async function fetchSuggestions(q) {
    // Return cached results immediately — no API call needed
    const cacheKey = `${q}|${proximity ? `${proximity.lng},${proximity.lat}` : "ip"}`;
    if (cacheRef.current.has(cacheKey)) {
      setResults(cacheRef.current.get(cacheKey));
      setOpen(true);
      return;
    }

    // Cancel any in-flight request before starting a new one
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    const params = new URLSearchParams({
      access_token: MAPBOX_TOKEN,
      types,
      limit: "6",
      language: "en",
    });
    // Always bias results — use GPS coords if available, fall back to IP-based proximity
    params.set(
      "proximity",
      proximity ? `${proximity.lng},${proximity.lat}` : "ip"
    );

    try {
      const res     = await fetch(`${GEOCODING_URL}/${encodeURIComponent(q)}.json?${params}`, { signal: abortRef.current.signal });
      const data    = await res.json();
      const features = data.features ?? [];
      cacheRef.current.set(cacheKey, features);
      setResults(features);
      setOpen(true);
    } catch (err) {
      if (err.name !== "AbortError") setResults([]);
    }
  }

  function handleSelect(feature) {
    onSelect(feature);
    setQuery(feature.place_name);
    setResults([]);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={handleInput}
          placeholder={placeholder}
          autoComplete="off"
          className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      {open && results.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md overflow-hidden">
          {results.map((feature) => (
            <li key={feature.id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(feature); }}
                className="flex w-full px-3 py-2.5 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <span className="truncate">{feature.place_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
